import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stack,
  Divider,
  Typography,
  LinearProgress,
} from '@mui/material';

import {
  Factory as FactoryIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  SwapVert as SwapVertIcon,
  FilterList as FilterListIcon,
  PlayArrow as PlayArrowIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  HourglassTop as HourglassTopIcon,
  PlayCircleFilled as PlayCircleFilledIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';

// --- MOCK DE ESTRUTURA DEPENDENTE (Empresa -> Filial -> Estágio) ---
const MOCK_EMPRESAS = [
  { id: '1', nome: 'Empresa 1 - IMIC Matriz' },
  { id: '2', nome: 'Empresa 2 - IMIC Filial Sul' },
];

const MOCK_FILIAIS = [
  { id: '1', empresaId: '1', nome: 'Filial 01 - Usinagem & Caldeiraria' },
  { id: '2', empresaId: '1', nome: 'Filial 02 - Fundição' },
  { id: '3', empresaId: '2', nome: 'Filial 03 - Montagem Externa' },
];

const MOCK_ESTAGIOS = [
  { id: '100', filialId: '1', nome: 'Estágio 100 - Prep. Matéria-Prima' },
  { id: '200', filialId: '1', nome: 'Estágio 200 - Corte / Dobra' },
  { id: '300', filialId: '1', nome: 'Estágio 300 - Usinagem CNC' },
  { id: '400', filialId: '2', nome: 'Estágio 400 - Moldagem e Fusão' },
  { id: '500', filialId: '3', nome: 'Estágio 500 - Ajuste Freqüência' },
];

// --- MOCK DE DADOS DE EXEMPLO ---
const MOCK_OPS = [
  {
    id: 'OP-2026-001',
    origem: 'VENDAS',
    op: 'OP-2026-001',
    produtoDescricao: 'PROD-1020 - Flange de Aço Inox 316L',
    pedido: 'PED-9840',
    cliente: 'Metalúrgica Vulcan S.A.',
    nome: 'Projeto Expansão Linha A',
    itemPedido: '01',
    qtdPrevista: 150,
    situacaoEstagio: 'EM ANDAMENTO',
    prioridade: 'ALTA',
    sequencia: '02/05',
    roteiro: 'ROT-CORTE-USINAGEM',
    inicioPrevisto: '2026-07-20',
    fimPrevisto: '2026-07-22', // Data passada = em atraso
    inicioReal: '20/07/2026',
    fimReal: '-',
    horaInicio: '07:30',
    horaFim: '-',
    centroRecurso: 'CR-300 TORNO CNC',
    tempoPrevisto: '18h 30m',
    tempoPrevistoHoras: 18.5,
    pesoUnitario: '4.85',
    unMed: 'UN',
    movimentos: [
      {
        id: 1,
        codOperador: 'OP-102',
        nomeOperador: 'Carlos Eduardo',
        horaInicio: '07:30',
        horaFim: '11:30',
        qtdProduzida: 50,
        qtdRefugo: 1,
      },
      {
        id: 2,
        codOperador: 'OP-102',
        nomeOperador: 'Carlos Eduardo',
        horaInicio: '12:30',
        horaFim: '16:00',
        qtdProduzida: 45,
        qtdRefugo: 0,
      },
    ],
  },
  {
    id: 'OP-2026-002',
    origem: 'PCP',
    op: 'OP-2026-002',
    produtoDescricao: 'PROD-3050 - Eixo de Transmissão Bi-Apoiado',
    pedido: 'PED-9812',
    cliente: 'Indústria de Tratores do Brasil',
    nome: 'Fornecimento Mensal Contrato',
    itemPedido: '03',
    qtdPrevista: 80,
    situacaoEstagio: 'AGUARDANDO',
    prioridade: 'MÉDIA',
    sequencia: '01/04',
    roteiro: 'ROT-TORNO-SOLDA',
    inicioPrevisto: '2026-07-24',
    fimPrevisto: '2026-07-28',
    inicioReal: '-',
    fimReal: '-',
    horaInicio: '-',
    horaFim: '-',
    centroRecurso: 'CR-105 CORTE PLASMA',
    tempoPrevisto: '12h 00m',
    tempoPrevistoHoras: 12.0,
    pesoUnitario: '12.30',
    unMed: 'KG',
    movimentos: [],
  },
  {
    id: 'OP-2026-003',
    origem: 'VENDAS',
    op: 'OP-2026-003',
    produtoDescricao: 'PROD-5010 - Engrenagem Cônica Z32',
    pedido: 'PED-9901',
    cliente: 'Usinagem Santa Luzia',
    nome: 'Manutenção Preditiva Redutor',
    itemPedido: '02',
    qtdPrevista: 30,
    situacaoEstagio: 'EM ANDAMENTO',
    prioridade: 'ALTA',
    sequencia: '03/03',
    roteiro: 'ROT-USINAGEM-DENTADORA',
    inicioPrevisto: '2026-07-23',
    fimPrevisto: '2026-07-26',
    inicioReal: '23/07/2026',
    fimReal: '-',
    horaInicio: '08:00',
    horaFim: '-',
    centroRecurso: 'CR-300 TORNO CNC',
    tempoPrevisto: '08h 00m',
    tempoPrevistoHoras: 8.0,
    pesoUnitario: '2.10',
    unMed: 'UN',
    movimentos: [],
  },
];

const CAPACIDADE_TURNO_HORAS = 40.0;

export default function DashboardMonitoramentoOPMUI() {
  // --- FILTROS DEPENDENTES / CASCATA (Linha 1) ---
  const [empresa, setEmpresa] = useState('1');
  const [filial, setFilial] = useState('1');
  const [estagio, setEstagio] = useState('300');

  // Listas filtradas dinamicamente
  const filiaisDisponiveis = useMemo(() => {
    return MOCK_FILIAIS.filter(f => f.empresaId === empresa);
  }, [empresa]);

  const estagiosDisponiveis = useMemo(() => {
    return MOCK_ESTAGIOS.filter(e => e.filialId === filial);
  }, [filial]);

  // Handlers para propagar alterações nas seleções pai
  const handleEmpresaChange = e => {
    const novaEmpresa = e.target.value;
    setEmpresa(novaEmpresa);

    const filiaisDaNovaEmpresa = MOCK_FILIAIS.filter(f => f.empresaId === novaEmpresa);
    if (filiaisDaNovaEmpresa.length > 0) {
      const primeiraFilial = filiaisDaNovaEmpresa[0].id;
      setFilial(primeiraFilial);

      const estagiosDaNovaFilial = MOCK_ESTAGIOS.filter(est => est.filialId === primeiraFilial);
      setEstagio(estagiosDaNovaFilial.length > 0 ? estagiosDaNovaFilial[0].id : '');
    } else {
      setFilial('');
      setEstagio('');
    }
  };

  const handleFilialChange = e => {
    const novaFilial = e.target.value;
    setFilial(novaFilial);

    const estagiosDaNovaFilial = MOCK_ESTAGIOS.filter(est => est.filialId === novaFilial);
    setEstagio(estagiosDaNovaFilial.length > 0 ? estagiosDaNovaFilial[0].id : '');
  };

  // --- FILTROS MÚLTIPLOS / DETALHADOS (Linha 2) ---
  const [filtroOrigem, setFiltroOrigem] = useState('');
  const [filtroOP, setFiltroOP] = useState('');
  const [filtroProduto, setFiltroProduto] = useState('');
  const [filtroPedido, setFiltroPedido] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroInicioPrevisto, setFiltroInicioPrevisto] = useState('');
  const [filtroFimPrevisto, setFiltroFimPrevisto] = useState('');
  const [filtroCentroRecurso, setFiltroCentroRecurso] = useState('');

  // Novos Filtros solicitados:
  const [filtroStatusOp, setFiltroStatusOp] = useState(''); // AGUARDANDO, EM ANDAMENTO, CONCLUÍDO
  const [filtroPrazoOp, setFiltroPrazoOp] = useState(''); // EM_ATRASO, EM_DIA

  // --- ORDENAÇÃO (Linha 3) ---
  const [campoOrdenacao, setCampoOrdenacao] = useState('op');
  const [ordemCrescente, setOrdemCrescente] = useState(true);

  // --- MODAL MOVIMENTOS ---
  const [opModal, setOpModal] = useState(null);

  // Data base de hoje
  const hojeStr = new Date().toISOString().split('T')[0];

  // CÁLCULO DOS KPIS PARA O DASHBOARD (Calculado em cima de TODAS as OPs do escopo de empresa/filial)
  const kpis = useMemo(() => {
    const totalOps = MOCK_OPS.length;
    let emAtraso = 0;
    let aguardando = 0;
    let emAndamento = 0;
    let tempoPrevistoTotal = 0;

    MOCK_OPS.forEach(op => {
      if (op.situacaoEstagio === 'AGUARDANDO') aguardando++;
      if (op.situacaoEstagio === 'EM ANDAMENTO') emAndamento++;

      const estaEmAtraso = op.situacaoEstagio !== 'CONCLUÍDO' && op.fimPrevisto < hojeStr;
      if (estaEmAtraso) emAtraso++;

      tempoPrevistoTotal += op.tempoPrevistoHoras || 0;
    });

    const percentualCarga = Math.min(Math.round((tempoPrevistoTotal / CAPACIDADE_TURNO_HORAS) * 100), 100);

    return {
      totalOps,
      emAtraso,
      pctAtraso: totalOps > 0 ? ((emAtraso / totalOps) * 100).toFixed(0) : 0,
      aguardando,
      pctAguardando: totalOps > 0 ? ((aguardando / totalOps) * 100).toFixed(0) : 0,
      emAndamento,
      pctAndamento: totalOps > 0 ? ((emAndamento / totalOps) * 100).toFixed(0) : 0,
      tempoPrevistoTotal,
      capacidadeTotal: CAPACIDADE_TURNO_HORAS,
      percentualCarga,
    };
  }, [hojeStr]);

  // FILTRAGEM FINAL DA LISTA DE CARDS
  const opsFiltradas = useMemo(() => {
    return MOCK_OPS.filter(item => {
      const matchOrigem = !filtroOrigem || item.origem.toLowerCase().includes(filtroOrigem.toLowerCase());
      const matchOP = !filtroOP || item.op.toLowerCase().includes(filtroOP.toLowerCase());
      const matchProduto = !filtroProduto || item.produtoDescricao.toLowerCase().includes(filtroProduto.toLowerCase());
      const matchPedido = !filtroPedido || item.pedido.toLowerCase().includes(filtroPedido.toLowerCase());
      const matchCliente = !filtroCliente || item.cliente.toLowerCase().includes(filtroCliente.toLowerCase());
      const matchCR =
        !filtroCentroRecurso || item.centroRecurso.toLowerCase().includes(filtroCentroRecurso.toLowerCase());
      const matchIni = !filtroInicioPrevisto || item.inicioPrevisto === filtroInicioPrevisto;
      const matchFim = !filtroFimPrevisto || item.fimPrevisto === filtroFimPrevisto;

      // Filtro Status OP
      const matchStatus = !filtroStatusOp || item.situacaoEstagio === filtroStatusOp;

      // Filtro Prazo OP
      const estaEmAtraso = item.situacaoEstagio !== 'CONCLUÍDO' && item.fimPrevisto < hojeStr;
      const matchPrazo = !filtroPrazoOp || (filtroPrazoOp === 'EM_ATRASO' ? estaEmAtraso : !estaEmAtraso);

      return (
        matchOrigem &&
        matchOP &&
        matchProduto &&
        matchPedido &&
        matchCliente &&
        matchCR &&
        matchIni &&
        matchFim &&
        matchStatus &&
        matchPrazo
      );
    }).sort((a, b) => {
      const valA = a[campoOrdenacao] || '';
      const valB = b[campoOrdenacao] || '';
      if (valA < valB) return ordemCrescente ? -1 : 1;
      if (valA > valB) return ordemCrescente ? 1 : -1;
      return 0;
    });
  }, [
    filtroOrigem,
    filtroOP,
    filtroProduto,
    filtroPedido,
    filtroCliente,
    filtroCentroRecurso,
    filtroInicioPrevisto,
    filtroFimPrevisto,
    filtroStatusOp,
    filtroPrazoOp,
    campoOrdenacao,
    ordemCrescente,
    hojeStr,
  ]);

  // Ações de clique nos Cards de KPI para filtrar a lista
  const handleFiltrarPorKpi = tipo => {
    if (tipo === 'TOTAL') {
      setFiltroStatusOp('');
      setFiltroPrazoOp('');
    } else if (tipo === 'ATRASO') {
      setFiltroPrazoOp('EM_ATRASO');
      setFiltroStatusOp('');
    } else if (tipo === 'AGUARDANDO') {
      setFiltroStatusOp('AGUARDANDO');
      setFiltroPrazoOp('');
    } else if (tipo === 'ANDAMENTO') {
      setFiltroStatusOp('EM ANDAMENTO');
      setFiltroPrazoOp('');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* CABEÇALHO */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <FactoryIcon color="primary" />
            Monitoramento de OPs / OS - Chão de Fábrica
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Acompanhamento em tempo real das ordens em produção
          </Typography>
        </Box>
      </Stack>

      {/* ÁREA DE FILTROS */}
      <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        {/* LINHA 1: FILTROS ÚNICOS (DEPENDENTES) */}
        <Typography
          variant="caption"
          color="primary"
          sx={{ fontWeight: 'bold', textTransform: 'uppercase', tracking: 1, display: 'block', mb: 1.5 }}
        >
          Filtros Principais (Hierárquicos)
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Empresa</InputLabel>
              <Select value={empresa} label="Empresa" onChange={handleEmpresaChange}>
                {MOCK_EMPRESAS.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" disabled={filiaisDisponiveis.length === 0}>
              <InputLabel>Filial</InputLabel>
              <Select value={filial} label="Filial" onChange={handleFilialChange}>
                {filiaisDisponiveis.map(f => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small" disabled={estagiosDisponiveis.length === 0}>
              <InputLabel>Estágio</InputLabel>
              <Select value={estagio} label="Estágio" onChange={e => setEstagio(e.target.value)}>
                {estagiosDisponiveis.map(est => (
                  <MenuItem key={est.id} value={est.id}>
                    {est.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* LINHA 2: FILTROS MÚLTIPLOS / DETALHADOS (INCLUI STATUS E PRAZO) */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 'bold', textTransform: 'uppercase', display: 'block', mb: 1.5 }}
        >
          Filtros Avançados (Múltiplos)
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={3} md={1.2}>
            <TextField
              fullWidth
              size="small"
              label="Origem"
              value={filtroOrigem}
              onChange={e => setFiltroOrigem(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.2}>
            <TextField fullWidth size="small" label="OP" value={filtroOP} onChange={e => setFiltroOP(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={1.6}>
            <TextField
              fullWidth
              size="small"
              label="Produto"
              value={filtroProduto}
              onChange={e => setFiltroProduto(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.2}>
            <TextField
              fullWidth
              size="small"
              label="Pedido"
              value={filtroPedido}
              onChange={e => setFiltroPedido(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.4}>
            <TextField
              fullWidth
              size="small"
              label="Cliente"
              value={filtroCliente}
              onChange={e => setFiltroCliente(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Início Previsto"
              InputLabelProps={{ shrink: true }}
              value={filtroInicioPrevisto}
              onChange={e => setFiltroInicioPrevisto(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Fim Previsto"
              InputLabelProps={{ shrink: true }}
              value={filtroFimPrevisto}
              onChange={e => setFiltroFimPrevisto(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.4}>
            <TextField
              fullWidth
              size="small"
              label="Centro Recurso"
              value={filtroCentroRecurso}
              onChange={e => setFiltroCentroRecurso(e.target.value)}
            />
          </Grid>

          {/* NOVOS FILTROS: STATUS OP E SITUAÇÃO DO PRAZO */}
          <Grid item xs={6} sm={3} md={1.4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status OP</InputLabel>
              <Select value={filtroStatusOp} label="Status OP" onChange={e => setFiltroStatusOp(e.target.value)}>
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                <MenuItem value="AGUARDANDO">Aguardando</MenuItem>
                <MenuItem value="EM ANDAMENTO">Em Andamento</MenuItem>
                <MenuItem value="CONCLUÍDO">Concluído</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3} md={1.0}>
            <FormControl fullWidth size="small">
              <InputLabel>Prazo</InputLabel>
              <Select value={filtroPrazoOp} label="Prazo" onChange={e => setFiltroPrazoOp(e.target.value)}>
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                <MenuItem value="EM_ATRASO">Em Atraso</MenuItem>
                <MenuItem value="EM_DIA">Em Dia</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* ========================================================================= */}
      {/* 📊 DASHBOARD DE KPIS INTERATIVO (PADRONIZADO EM ALTURA E COM PORCENTAGENS) */}
      {/* ========================================================================= */}
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
        {/* CARD 1: TOTAL DE OPS */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={!filtroStatusOp && !filtroPrazoOp ? 4 : 1}
            onClick={() => handleFiltrarPorKpi('TOTAL')}
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justify: 'space-between',
              cursor: 'pointer',
              borderLeft: '4px solid',
              borderLeftColor: 'primary.main',
              bgcolor: !filtroStatusOp && !filtroPrazoOp ? 'action.selected' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': { elevation: 4, transform: 'translateY(-2px)' },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Total de OPs
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 0.5 }}>
                  {kpis.totalOps}
                </Typography>
              </Box>
              <AssignmentIcon color="primary" sx={{ fontSize: 32, opacity: 0.8 }} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
              100% da base filtrada
            </Typography>
          </Paper>
        </Grid>

        {/* CARD 2: OPS EM ATRASO */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={filtroPrazoOp === 'EM_ATRASO' ? 4 : 1}
            onClick={() => handleFiltrarPorKpi('ATRASO')}
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justify: 'space-between',
              cursor: 'pointer',
              borderLeft: '4px solid',
              borderLeftColor: 'error.main',
              bgcolor: filtroPrazoOp === 'EM_ATRASO' ? 'action.selected' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': { elevation: 4, transform: 'translateY(-2px)' },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Em Atraso
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main', mt: 0.5 }}>
                  {kpis.emAtraso}
                </Typography>
              </Box>
              <WarningIcon color="error" sx={{ fontSize: 32, opacity: 0.8 }} />
            </Stack>
            <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block', fontWeight: 'bold' }}>
              {kpis.pctAtraso}% do total em atraso
            </Typography>
          </Paper>
        </Grid>

        {/* CARD 3: TOTAL AGUARDANDO */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={filtroStatusOp === 'AGUARDANDO' ? 4 : 1}
            onClick={() => handleFiltrarPorKpi('AGUARDANDO')}
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justify: 'space-between',
              cursor: 'pointer',
              borderLeft: '4px solid',
              borderLeftColor: 'warning.main',
              bgcolor: filtroStatusOp === 'AGUARDANDO' ? 'action.selected' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': { elevation: 4, transform: 'translateY(-2px)' },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Aguardando
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main', mt: 0.5 }}>
                  {kpis.aguardando}
                </Typography>
              </Box>
              <HourglassTopIcon color="warning" sx={{ fontSize: 32, opacity: 0.8 }} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
              {kpis.pctAguardando}% do total parado
            </Typography>
          </Paper>
        </Grid>

        {/* CARD 4: TOTAL EM ANDAMENTO */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={filtroStatusOp === 'EM ANDAMENTO' ? 4 : 1}
            onClick={() => handleFiltrarPorKpi('ANDAMENTO')}
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justify: 'space-between',
              cursor: 'pointer',
              borderLeft: '4px solid',
              borderLeftColor: 'success.main',
              bgcolor: filtroStatusOp === 'EM ANDAMENTO' ? 'action.selected' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': { elevation: 4, transform: 'translateY(-2px)' },
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Em Andamento
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main', mt: 0.5 }}>
                  {kpis.emAndamento}
                </Typography>
              </Box>
              <PlayCircleFilledIcon color="success" sx={{ fontSize: 32, opacity: 0.8 }} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
              {kpis.pctAndamento}% do total na fábrica
            </Typography>
          </Paper>
        </Grid>

        {/* CARD 5: TEMPO PREVISTO X CAPACIDADE (MESMA ALTURA DOS OUTROS) */}
        <Grid item xs={12} sm={12} md={2.4}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justify: 'space-between',
              bgcolor: 'background.paper',
              borderLeft: '4px solid',
              borderLeftColor: 'info.main',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Previsto x Capacidade
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5, lineHeight: 1.2 }}>
                  {kpis.tempoPrevistoTotal.toFixed(1)}h / {kpis.capacidadeTotal}h
                </Typography>
              </Box>
              <SpeedIcon color="info" sx={{ fontSize: 30, opacity: 0.8 }} />
            </Stack>

            <Box sx={{ width: '100%', mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={kpis.percentualCarga}
                color={kpis.percentualCarga > 90 ? 'error' : kpis.percentualCarga > 75 ? 'warning' : 'info'}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'right', mt: 0.5, fontSize: '0.70rem', fontWeight: 'bold' }}
              >
                {kpis.percentualCarga}% de carga ocupada
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* LINHA DE ORDENAÇÃO */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <FilterListIcon color="action" fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Ordenar por:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select value={campoOrdenacao} onChange={e => setCampoOrdenacao(e.target.value)}>
                <MenuItem value="op">OP</MenuItem>
                <MenuItem value="prioridade">Prioridade</MenuItem>
                <MenuItem value="situacaoEstagio">Situação Estágio</MenuItem>
                <MenuItem value="inicioPrevisto">Início Previsto</MenuItem>
                <MenuItem value="fimPrevisto">Fim Previsto</MenuItem>
                <MenuItem value="cliente">Cliente</MenuItem>
                <MenuItem value="centroRecurso">Centro de Recurso</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Button
            variant="outlined"
            size="small"
            startIcon={<SwapVertIcon />}
            onClick={() => setOrdemCrescente(!ordemCrescente)}
          >
            {ordemCrescente ? 'Crescente (A-Z / 1-9)' : 'Decrescente (Z-A / 9-1)'}
          </Button>
        </Stack>
      </Paper>

      {/* LISTA DE CARDS DE OPs */}
      <Stack spacing={2}>
        {opsFiltradas.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Nenhuma Ordem de Produção encontrada para os filtros aplicados.
            </Typography>
          </Paper>
        ) : (
          opsFiltradas.map(opItem => (
            <Card
              key={opItem.id}
              elevation={2}
              sx={{
                borderRadius: 2,
                borderLeft: '6px solid',
                borderLeftColor:
                  opItem.prioridade === 'ALTA'
                    ? 'error.main'
                    : opItem.prioridade === 'MÉDIA'
                      ? 'warning.main'
                      : 'success.main',
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* CABEÇALHO DO CARD */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ sm: 'center' }}
                  spacing={1}
                  sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Chip label={opItem.op} color="primary" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} />
                    <Chip label={`ORIGEM: ${opItem.origem}`} size="small" variant="outlined" />
                    <Chip
                      label={`PRIORIDADE: ${opItem.prioridade}`}
                      size="small"
                      color={
                        opItem.prioridade === 'ALTA' ? 'error' : opItem.prioridade === 'MÉDIA' ? 'warning' : 'success'
                      }
                    />
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      icon={<PlayArrowIcon />}
                      label={opItem.situacaoEstagio}
                      color={opItem.situacaoEstagio === 'EM ANDAMENTO' ? 'success' : 'default'}
                      variant={opItem.situacaoEstagio === 'EM ANDAMENTO' ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />

                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => setOpModal(opItem)}
                      sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    >
                      Movimentos
                    </Button>
                  </Stack>
                </Stack>

                {/* CORPO DO CARD - TODOS OS 22 CAMPOS AGRUPADOS */}
                <Grid container spacing={2}>
                  {/* BLOCO 1: PRODUTO E COMERCIAL */}
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: 'action.hover' }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 'bold', display: 'block' }}
                      >
                        PRODUTO / DESCRIÇÃO:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {opItem.produtoDescricao}
                      </Typography>

                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            CLIENTE:
                          </Typography>
                          <Typography variant="body2" noWrap>
                            {opItem.cliente}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            NOME PROJETO:
                          </Typography>
                          <Typography variant="body2" noWrap>
                            {opItem.nome}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            PEDIDO:
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {opItem.pedido}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            ITEM PEDIDO:
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {opItem.itemPedido}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* BLOCO 2: RECURSOS, ROTEIRO E QUANTIDADES */}
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: 'action.hover' }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            CENTRO RECURSO:
                          </Typography>
                          <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                            {opItem.centroRecurso}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            ROTEIRO / SEQ:
                          </Typography>
                          <Typography variant="body2">
                            {opItem.roteiro} ({opItem.sequencia})
                          </Typography>
                        </Grid>

                        <Grid item xs={4} sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            QTD PREVISTA:
                          </Typography>
                          <Typography variant="body1" color="success.main" sx={{ fontWeight: 'bold' }}>
                            {opItem.qtdPrevista}
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            PESO UNIT.:
                          </Typography>
                          <Typography variant="body2">{opItem.pesoUnitario} kg</Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            UN. MED.:
                          </Typography>
                          <Typography variant="body2">{opItem.unMed}</Typography>
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            TEMPO PREVISTO ESTÁGIO:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {opItem.tempoPrevisto}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* BLOCO 3: DATAS E HORÁRIOS */}
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: 'action.hover' }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            INÍCIO PREVISTO:
                          </Typography>
                          <Typography variant="body2">{opItem.inicioPrevisto}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            FIM PREVISTO:
                          </Typography>
                          <Typography
                            variant="body2"
                            color={
                              opItem.situacaoEstagio !== 'CONCLUÍDO' && opItem.fimPrevisto < hojeStr
                                ? 'error.main'
                                : 'text.primary'
                            }
                            sx={{
                              fontWeight:
                                opItem.situacaoEstagio !== 'CONCLUÍDO' && opItem.fimPrevisto < hojeStr
                                  ? 'bold'
                                  : 'normal',
                            }}
                          >
                            {opItem.fimPrevisto}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            INÍCIO REAL:
                          </Typography>
                          <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                            {opItem.inicioReal}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            FIM REAL:
                          </Typography>
                          <Typography variant="body2">{opItem.fimReal}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            HORA INÍCIO:
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {opItem.horaInicio}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            HORA FIM:
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {opItem.horaFim}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))
        )}
      </Stack>

      {/* MODAL DE MOVIMENTOS DA OP */}
      {opModal && (
        <Dialog open={Boolean(opModal)} onClose={() => setOpModal(null)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <ScheduleIcon color="primary" />
                Movimentos da {opModal.op}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {opModal.produtoDescricao}
              </Typography>
            </Box>
            <IconButton onClick={() => setOpModal(null)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {opModal.movimentos.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Nenhum movimento registrado até o momento para esta OP.</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'action.hover' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Cód. Operador</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Nome Operador</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        Hora Início
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                        Hora Fim
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Qtd Produzida
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Qtd Refugo
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {opModal.movimentos.map(mov => (
                      <TableRow key={mov.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'primary.main' }}>
                          {mov.codOperador}
                        </TableCell>
                        <TableCell>{mov.nomeOperador}</TableCell>
                        <TableCell align="center" sx={{ fontFamily: 'monospace' }}>
                          {mov.horaInicio}
                        </TableCell>
                        <TableCell align="center" sx={{ fontFamily: 'monospace' }}>
                          {mov.horaFim}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          {mov.qtdProduzida}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                          {mov.qtdRefugo}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={3}>
              <Typography variant="body2">
                Total Produzido:{' '}
                <strong style={{ color: '#2e7d32' }}>
                  {opModal.movimentos.reduce((acc, m) => acc + m.qtdProduzida, 0)}
                </strong>
              </Typography>
              <Typography variant="body2">
                Total Refugo:{' '}
                <strong style={{ color: '#d32f2f' }}>
                  {opModal.movimentos.reduce((acc, m) => acc + m.qtdRefugo, 0)}
                </strong>
              </Typography>
            </Stack>

            <Button onClick={() => setOpModal(null)} variant="outlined">
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
